@file:Suppress(
  "unused",
  "RedundantVisibilityModifier",
  "RemoveRedundantBackticks",
  "FunctionName",
  "SENSELESS_COMPARISON",
  "RemoveRedundantCallsOfConversionMethods",
)
@file:OptIn(
  ExperimentalTime::class,
  ExperimentalSerializationApi::class,
)

package com.siriusxm.unifiedcontent.cmc

import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.`header`
import io.ktor.client.request.setBody
import io.ktor.http.HttpStatusCode
import io.ktor.http.appendPathSegments
import io.ktor.http.takeFrom
import io.ktor.server.application.ApplicationCall
import io.ktor.server.application.call
import io.ktor.server.request.receive
import io.ktor.server.response.respond
import io.ktor.server.routing.Route
import io.ktor.server.util.getOrFail
import kotlin.Boolean
import kotlin.Double
import kotlin.IllegalArgumentException
import kotlin.Int
import kotlin.Long
import kotlin.OptIn
import kotlin.String
import kotlin.Suppress
import kotlin.collections.List
import kotlin.lazy
import kotlin.time.ExperimentalTime
import kotlin.time.Instant
import kotlinx.serialization.ExperimentalSerializationApi
import kotlinx.serialization.KSerializer
import kotlinx.serialization.Serializable
import kotlinx.serialization.descriptors.PrimitiveKind
import kotlinx.serialization.descriptors.PrimitiveSerialDescriptor
import kotlinx.serialization.descriptors.SerialDescriptor
import kotlinx.serialization.encoding.Decoder
import kotlinx.serialization.encoding.Encoder
import kotlinx.serialization.json.JsonElement
import smithy4kt.GenericClientError
import smithy4kt.GenericServerError
import smithy4kt.SmithyClientError
import smithy4kt.SmithyEnum
import smithy4kt.SmithyEnumCompanion
import smithy4kt.SmithyServerError
import smithy4kt.SmithyUnknown
import io.ktor.client.request.`get` as requestGet
import io.ktor.client.request.delete as requestDelete
import io.ktor.client.request.post as requestPost
import io.ktor.client.request.put as requestPut
import io.ktor.server.routing.`get` as routingGet
import io.ktor.server.routing.delete as routingDelete
import io.ktor.server.routing.post as routingPost
import io.ktor.server.routing.put as routingPut

@Serializable
public data class AddFavoriteInput(
  /**
   * The entity reference in format "<type>:<id>" (e.g., "show:12345")
   */
  public val body: AddFavoritePayload,
)

@Serializable
public data class AddFavoriteOutput(
  /**
   * Whether the operation succeeded
   */
  public val success: Boolean? = null,
)

@Serializable
public data class AddFavoritePayload(
  /**
   * The entity reference in format "<type>:<id>" (e.g., "show:12345")
   */
  public val entityId: String,
)

/**
 * Aggregator returned the same ID under more than one entity type, so no deterministic adapter can
 * be selected.
 */
@Serializable
public data class AmbiguousPlaybackEntityData(
  public val message: String,
)

/**
 * Aggregator returned the same ID under more than one entity type, so no deterministic adapter can
 * be selected.
 */
public class AmbiguousPlaybackEntity(
  public val `data`: AmbiguousPlaybackEntityData,
) : SmithyClientError(data.message, httpStatusCode = 409) {
  override val message: String
    get() = data.message

  public constructor(message: String) : this(AmbiguousPlaybackEntityData(message))
}

/**
 * This service provides endpoints for retrieving user-related content like favorites
 */
public class CMCService(
  public val baseUrl: String,
  public val client: HttpClient,
) {
  /**
   * Lazy-initialized Chat resource client
   */
  public val chat: Chat by lazy { Chat(baseUrl, client) }

  /**
   * Lazy-initialized ChatTranscripts resource client
   */
  public val chatTranscripts: ChatTranscripts by lazy { ChatTranscripts(baseUrl, client) }

  /**
   * Lazy-initialized Favorites resource client
   */
  public val favorites: Favorites by lazy { Favorites(baseUrl, client) }

  /**
   * Lazy-initialized News resource client
   */
  public val news: News by lazy { News(baseUrl, client) }

  /**
   * Lazy-initialized Operations resource client
   */
  public val operations: Operations by lazy { Operations(baseUrl, client) }

  /**
   * Retrieve an entity from an external service.
   * @serviceId The identifier of the service, or 'all' for all services
   * @region The region to get the data from. Defaults to the main region. If `all`
   * is passed, returns data from all available regions (typically 3)
   */
  public suspend fun getServiceEntity(
    serviceId: String,
    region: String? = null,
    stage: String? = null,
    body: GetServiceEntityPayload,
  ): ServiceEntityList {
    val result = client.requestPost {
      url {
        takeFrom(baseUrl)
        appendPathSegments("services")
        appendPathSegments(serviceId.toString(), encodeSlash = true)
        appendPathSegments("entities")
        if (region != null) {
          parameters.append("region", region.toString())
        }
        if (stage != null) {
          parameters.append("stage", stage.toString())
        }
      }
      `header`("Content-Type", "application/json")
      setBody(body)
    }
    if (result.status.value >= 400) {
      if (result.status.value < 500) {
        throw GenericClientError("Client error: " + result.status, result.status.value)
      } else {
        throw GenericServerError("Server error: " + result.status, result.status.value)
      }
    }
    return result.body<ServiceEntityList>()
  }

  /**
   * Prepare playback from details the caller supplies directly, without an Aggregator lookup.
   *
   * This exists for content that cannot be resolved through Aggregator — most notably an expired /
   * archived on-demand episode whose media still lives in the AOD bucket even though its aggregator
   * entity is gone. The caller provides the playback details; the server signs the same CloudFront
   * policy and returns the same normalized description and cookies as `PreparePlayback` would. The
   * server still constrains signing to its configured AOD origin/key layout, so this is not an open
   * signer for arbitrary URLs.
   */
  public suspend fun prepareDirectPlayback(body: DirectPlaybackRequest): PlaybackDescriptor {
    val result = client.requestPost {
      url {
        takeFrom(baseUrl)
        appendPathSegments("playback")
        appendPathSegments("direct")
      }
      `header`("Content-Type", "application/json")
      setBody(body)
    }
    if (result.status.value >= 400) {
      when (result.status.value) {
        502 -> {
          val errorData = result.body<PlaybackDependencyFailedData>()
          throw PlaybackDependencyFailed(errorData)
        }
        500 -> {
          val errorData = result.body<PlaybackNotConfiguredData>()
          throw PlaybackNotConfigured(errorData)
        }
        400 -> {
          val errorData = result.body<UnsupportedOriginData>()
          throw UnsupportedOrigin(errorData)
        }
        400 -> {
          val errorData = result.body<UnsupportedPlaybackEntityData>()
          throw UnsupportedPlaybackEntity(errorData)
        }
        else -> {
          if (result.status.value < 500) {
            throw GenericClientError("Client error: " + result.status, result.status.value)
          } else {
            throw GenericServerError("Server error: " + result.status, result.status.value)
          }
        }
      }
    }
    return result.body<PlaybackDescriptor>()
  }

  /**
   * Prepare playback for a unified-content entity.
   *
   * The CMC server loads the authoritative entity from Aggregator using the caller's bearer token,
   * selects an entity-type-specific playback adapter, and returns a normalized description containing
   * display metadata and final playable streams. Callers do not submit entity JSON, playlist
   * coordinates, storage locations, or signing parameters.
   */
  public suspend fun preparePlayback(body: PlaybackRequest): PlaybackDescriptor {
    val result = client.requestPost {
      url {
        takeFrom(baseUrl)
        appendPathSegments("playback")
      }
      `header`("Content-Type", "application/json")
      setBody(body)
    }
    if (result.status.value >= 400) {
      when (result.status.value) {
        409 -> {
          val errorData = result.body<AmbiguousPlaybackEntityData>()
          throw AmbiguousPlaybackEntity(errorData)
        }
        502 -> {
          val errorData = result.body<PlaybackDependencyFailedData>()
          throw PlaybackDependencyFailed(errorData)
        }
        404 -> {
          val errorData = result.body<PlaybackEntityNotFoundData>()
          throw PlaybackEntityNotFound(errorData)
        }
        500 -> {
          val errorData = result.body<PlaybackNotConfiguredData>()
          throw PlaybackNotConfigured(errorData)
        }
        428 -> {
          val errorData = result.body<SubscriberConnectionRequiredData>()
          throw SubscriberConnectionRequired(errorData)
        }
        400 -> {
          val errorData = result.body<UnsupportedOriginData>()
          throw UnsupportedOrigin(errorData)
        }
        400 -> {
          val errorData = result.body<UnsupportedPlaybackEntityData>()
          throw UnsupportedPlaybackEntity(errorData)
        }
        else -> {
          if (result.status.value < 500) {
            throw GenericClientError("Client error: " + result.status, result.status.value)
          } else {
            throw GenericServerError("Server error: " + result.status, result.status.value)
          }
        }
      }
    }
    return result.body<PlaybackDescriptor>()
  }
}

/**
 * Resource for AI-powered chat conversations
 */
public class Chat(
  public val baseUrl: String,
  public val client: HttpClient,
) {
  /**
   * Delete a conversation
   */
  public suspend fun deleteChat(conversationId: String) {
    val result = client.requestDelete {
      url {
        takeFrom(baseUrl)
        appendPathSegments("chat")
        appendPathSegments(conversationId.toString(), encodeSlash = true)
      }
    }
    if (result.status.value >= 400) {
      when (result.status.value) {
        404 -> {
          val errorData = result.body<ChatNotFoundData>()
          throw ChatNotFound(errorData)
        }
        else -> {
          if (result.status.value < 500) {
            throw GenericClientError("Client error: " + result.status, result.status.value)
          } else {
            throw GenericServerError("Server error: " + result.status, result.status.value)
          }
        }
      }
    }
  }

  /**
   * Get the status and result of a conversation
   */
  public suspend fun getChat(conversationId: String): ChatConversation {
    val result = client.requestGet {
      url {
        takeFrom(baseUrl)
        appendPathSegments("chat")
        appendPathSegments(conversationId.toString(), encodeSlash = true)
      }
    }
    if (result.status.value >= 400) {
      when (result.status.value) {
        404 -> {
          val errorData = result.body<ChatNotFoundData>()
          throw ChatNotFound(errorData)
        }
        else -> {
          if (result.status.value < 500) {
            throw GenericClientError("Client error: " + result.status, result.status.value)
          } else {
            throw GenericServerError("Server error: " + result.status, result.status.value)
          }
        }
      }
    }
    return result.body<ChatConversation>()
  }

  /**
   * List recent conversations for the current user, most recent first
   * @limit Maximum number of conversations to return (default 20)
   */
  public suspend fun listChats(limit: Int? = null): ListChatsResponse {
    val result = client.requestGet {
      url {
        takeFrom(baseUrl)
        appendPathSegments("chat")
        if (limit != null) {
          parameters.append("limit", limit.toString())
        }
      }
    }
    if (result.status.value >= 400) {
      if (result.status.value < 500) {
        throw GenericClientError("Client error: " + result.status, result.status.value)
      } else {
        throw GenericServerError("Server error: " + result.status, result.status.value)
      }
    }
    return result.body<ListChatsResponse>()
  }

  /**
   * Start a new chat conversation. Returns immediately with a conversationId;
   * the LLM processes asynchronously. Poll GetChat to retrieve the result.
   */
  public suspend fun startChat(body: StartChatPayload): StartChatResponse {
    val result = client.requestPost {
      url {
        takeFrom(baseUrl)
        appendPathSegments("chat")
      }
      `header`("Content-Type", "application/json")
      setBody(body)
    }
    if (result.status.value >= 400) {
      if (result.status.value < 500) {
        throw GenericClientError("Client error: " + result.status, result.status.value)
      } else {
        throw GenericServerError("Server error: " + result.status, result.status.value)
      }
    }
    return result.body<StartChatResponse>()
  }
}

/**
 * Admin resource for reviewing full chat transcripts, including tool calls and results (UCA-1091).
 *
 * Distinct from Chat: those endpoints are per-user and return only role+content. These are gated by
 * the unified-content AVP action SXM::UnifiedContentManagementConsole_ViewChatTranscripts and expose
 * the complete agentic trace captured to S3. The `/chat-transcripts` prefix (not `/chat/...`) keeps
 * these off the same path space as GetChat's `/chat/{conversationId}`.
 */
public class ChatTranscripts(
  public val baseUrl: String,
  public val client: HttpClient,
) {
  /**
   * Get the full captured transcript for a conversation, including tool calls and results.
   */
  public suspend fun getChatTranscript(conversationId: String): ChatTranscript {
    val result = client.requestGet {
      url {
        takeFrom(baseUrl)
        appendPathSegments("chat-transcripts")
        appendPathSegments(conversationId.toString(), encodeSlash = true)
      }
    }
    if (result.status.value >= 400) {
      when (result.status.value) {
        404 -> {
          val errorData = result.body<ChatNotFoundData>()
          throw ChatNotFound(errorData)
        }
        else -> {
          if (result.status.value < 500) {
            throw GenericClientError("Client error: " + result.status, result.status.value)
          } else {
            throw GenericServerError("Server error: " + result.status, result.status.value)
          }
        }
      }
    }
    return result.body<ChatTranscript>()
  }

  /**
   * List recent conversations across all users, most recent first (admin review).
   * @limit Maximum number of conversations to return (default 50)
   * @userId Optional filter to a single user's conversations (Cognito sub)
   */
  public suspend fun listChatTranscripts(limit: Int? = null, userId: String? = null): ListChatTranscriptsResponse {
    val result = client.requestGet {
      url {
        takeFrom(baseUrl)
        appendPathSegments("chat-transcripts")
        if (limit != null) {
          parameters.append("limit", limit.toString())
        }
        if (userId != null) {
          parameters.append("userId", userId.toString())
        }
      }
    }
    if (result.status.value >= 400) {
      if (result.status.value < 500) {
        throw GenericClientError("Client error: " + result.status, result.status.value)
      } else {
        throw GenericServerError("Server error: " + result.status, result.status.value)
      }
    }
    return result.body<ListChatTranscriptsResponse>()
  }
}

/**
 * Resource for managing user favorites
 */
public class Favorites(
  public val baseUrl: String,
  public val client: HttpClient,
) {
  /**
   * Add an entity to the user's favorites list
   * @body The entity reference in format "<type>:<id>" (e.g., "show:12345")
   */
  public suspend fun addFavorite(body: AddFavoritePayload) {
    val result = client.requestPost {
      url {
        takeFrom(baseUrl)
        appendPathSegments("favorites")
      }
      `header`("Content-Type", "application/json")
      setBody(body)
    }
    if (result.status.value >= 400) {
      if (result.status.value < 500) {
        throw GenericClientError("Client error: " + result.status, result.status.value)
      } else {
        throw GenericServerError("Server error: " + result.status, result.status.value)
      }
    }
  }

  /**
   * Check if an entity is in the user's favorites
   * @entityId The entity reference to check
   */
  public suspend fun checkFavorite(entityId: String) {
    val result = client.requestGet {
      url {
        takeFrom(baseUrl)
        appendPathSegments("favorites")
        appendPathSegments(entityId.toString(), encodeSlash = true)
      }
    }
    if (result.status.value >= 400) {
      if (result.status.value < 500) {
        throw GenericClientError("Client error: " + result.status, result.status.value)
      } else {
        throw GenericServerError("Server error: " + result.status, result.status.value)
      }
    }
  }

  /**
   * Get all favorites for the current user
   */
  public suspend fun getFavorites(): FavoritesList {
    val result = client.requestGet {
      url {
        takeFrom(baseUrl)
        appendPathSegments("favorites")
      }
    }
    if (result.status.value >= 400) {
      if (result.status.value < 500) {
        throw GenericClientError("Client error: " + result.status, result.status.value)
      } else {
        throw GenericServerError("Server error: " + result.status, result.status.value)
      }
    }
    return result.body<FavoritesList>()
  }

  /**
   * Remove an entity from the user's favorites list
   * @entityId The entity reference to remove
   */
  public suspend fun removeFavorite(entityId: String) {
    val result = client.requestDelete {
      url {
        takeFrom(baseUrl)
        appendPathSegments("favorites")
        appendPathSegments(entityId.toString(), encodeSlash = true)
      }
    }
    if (result.status.value >= 400) {
      if (result.status.value < 500) {
        throw GenericClientError("Client error: " + result.status, result.status.value)
      } else {
        throw GenericServerError("Server error: " + result.status, result.status.value)
      }
    }
  }
}

/**
 * Resource for managing news items displayed on the home page and news feed
 */
public class News(
  public val baseUrl: String,
  public val client: HttpClient,
) {
  /**
   * Create a new news item (admin only)
   */
  public suspend fun createNewsItem(body: CreateNewsItemPayload): NewsItem {
    val result = client.requestPost {
      url {
        takeFrom(baseUrl)
        appendPathSegments("news")
      }
      `header`("Content-Type", "application/json")
      setBody(body)
    }
    if (result.status.value >= 400) {
      if (result.status.value < 500) {
        throw GenericClientError("Client error: " + result.status, result.status.value)
      } else {
        throw GenericServerError("Server error: " + result.status, result.status.value)
      }
    }
    return result.body<NewsItem>()
  }

  /**
   * Delete a news item (admin only)
   * @id The news item ID
   */
  public suspend fun deleteNewsItem(id: String) {
    val result = client.requestDelete {
      url {
        takeFrom(baseUrl)
        appendPathSegments("news")
        appendPathSegments(id.toString(), encodeSlash = true)
      }
    }
    if (result.status.value >= 400) {
      if (result.status.value < 500) {
        throw GenericClientError("Client error: " + result.status, result.status.value)
      } else {
        throw GenericServerError("Server error: " + result.status, result.status.value)
      }
    }
  }

  /**
   * Get a single news item by ID
   * @id The news item ID
   */
  public suspend fun getNewsItem(id: String): NewsItem {
    val result = client.requestGet {
      url {
        takeFrom(baseUrl)
        appendPathSegments("news")
        appendPathSegments(id.toString(), encodeSlash = true)
      }
    }
    if (result.status.value >= 400) {
      if (result.status.value < 500) {
        throw GenericClientError("Client error: " + result.status, result.status.value)
      } else {
        throw GenericServerError("Server error: " + result.status, result.status.value)
      }
    }
    return result.body<NewsItem>()
  }

  /**
   * List all news items (newest first)
   * @limit Maximum number of items to return (default 20)
   * @nextToken Token for pagination
   */
  public suspend fun listNewsItems(limit: Int? = null, nextToken: String? = null): ListNewsItemsResponse {
    val result = client.requestGet {
      url {
        takeFrom(baseUrl)
        appendPathSegments("news")
        if (limit != null) {
          parameters.append("limit", limit.toString())
        }
        if (nextToken != null) {
          parameters.append("nextToken", nextToken.toString())
        }
      }
    }
    if (result.status.value >= 400) {
      if (result.status.value < 500) {
        throw GenericClientError("Client error: " + result.status, result.status.value)
      } else {
        throw GenericServerError("Server error: " + result.status, result.status.value)
      }
    }
    return result.body<ListNewsItemsResponse>()
  }

  /**
   * Update a news item (admin only)
   * @id The news item ID
   */
  public suspend fun updateNewsItem(id: String, body: UpdateNewsItemPayload): NewsItem {
    val result = client.requestPut {
      url {
        takeFrom(baseUrl)
        appendPathSegments("news")
        appendPathSegments(id.toString(), encodeSlash = true)
      }
      `header`("Content-Type", "application/json")
      setBody(body)
    }
    if (result.status.value >= 400) {
      if (result.status.value < 500) {
        throw GenericClientError("Client error: " + result.status, result.status.value)
      } else {
        throw GenericServerError("Server error: " + result.status, result.status.value)
      }
    }
    return result.body<NewsItem>()
  }
}

/**
 * Provides operational data via DataDog integration.
 */
public class Operations(
  public val baseUrl: String,
  public val client: HttpClient,
) {
  public suspend fun getMonitors(
    tag: String,
    page: Int? = null,
    pageSize: Int? = null,
  ): ListMonitorsResponse {
    val result = client.requestGet {
      url {
        takeFrom(baseUrl)
        appendPathSegments("monitors")
        parameters.append("tag", tag.toString())
        if (page != null) {
          parameters.append("page", page.toString())
        }
        if (pageSize != null) {
          parameters.append("pageSize", pageSize.toString())
        }
      }
    }
    if (result.status.value >= 400) {
      if (result.status.value < 500) {
        throw GenericClientError("Client error: " + result.status, result.status.value)
      } else {
        throw GenericServerError("Server error: " + result.status, result.status.value)
      }
    }
    return result.body<ListMonitorsResponse>()
  }

  public suspend fun queryLogs(
    query: String,
    limit: Int? = null,
    startTs: Instant,
    endTs: Instant,
    paginationToken: String? = null,
    flex: Boolean? = null,
    service: String? = null,
    workload: String? = null,
    region: String? = null,
    environment: String? = null,
  ): QueryResponse {
    val result = client.requestGet {
      url {
        takeFrom(baseUrl)
        appendPathSegments("logs")
        parameters.append("query", query.toString())
        if (limit != null) {
          parameters.append("limit", limit.toString())
        }
        parameters.append("startTs", startTs.toString())
        parameters.append("endTs", endTs.toString())
        if (paginationToken != null) {
          parameters.append("paginationToken", paginationToken.toString())
        }
        if (flex != null) {
          parameters.append("flex", flex.toString())
        }
        if (service != null) {
          parameters.append("service", service.toString())
        }
        if (workload != null) {
          parameters.append("workload", workload.toString())
        }
        if (region != null) {
          parameters.append("region", region.toString())
        }
        if (environment != null) {
          parameters.append("environment", environment.toString())
        }
      }
    }
    if (result.status.value >= 400) {
      if (result.status.value < 500) {
        throw GenericClientError("Client error: " + result.status, result.status.value)
      } else {
        throw GenericServerError("Server error: " + result.status, result.status.value)
      }
    }
    return result.body<QueryResponse>()
  }

  /**
   * Query one or more DataDog metric timeseries and return the results
   * as a list of series with timestamped data points.
   */
  public suspend fun queryMetrics(body: MetricsQueryRequest): MetricsQueryResponse {
    val result = client.requestPost {
      url {
        takeFrom(baseUrl)
        appendPathSegments("metrics")
        appendPathSegments("query")
      }
      `header`("Content-Type", "application/json")
      setBody(body)
    }
    if (result.status.value >= 400) {
      if (result.status.value < 500) {
        throw GenericClientError("Client error: " + result.status, result.status.value)
      } else {
        throw GenericServerError("Server error: " + result.status, result.status.value)
      }
    }
    return result.body<MetricsQueryResponse>()
  }
}

/**
 * Generated server interface for Chat
 */
public interface ChatServer {
  public suspend fun ApplicationCall.deleteChat(conversationId: String): DeleteChatOutput

  public suspend fun ApplicationCall.getChat(conversationId: String): GetChatOutput

  /**
   * @limit Maximum number of conversations to return (default 20)
   */
  public suspend fun ApplicationCall.listChats(limit: Int? = null): ListChatsOutput

  public suspend fun ApplicationCall.startChat(body: StartChatPayload): StartChatOutput
}

/**
 * Generated server interface for ChatTranscripts
 */
public interface ChatTranscriptsServer {
  public suspend fun ApplicationCall.getChatTranscript(conversationId: String): GetChatTranscriptOutput

  /**
   * @limit Maximum number of conversations to return (default 50)
   * @userId Optional filter to a single user's conversations (Cognito sub)
   */
  public suspend fun ApplicationCall.listChatTranscripts(limit: Int? = null, userId: String? = null): ListChatTranscriptsOutput
}

/**
 * Generated server interface for Favorites
 */
public interface FavoritesServer {
  /**
   * @body The entity reference in format "<type>:<id>" (e.g., "show:12345")
   */
  public suspend fun ApplicationCall.addFavorite(body: AddFavoritePayload): AddFavoriteOutput

  /**
   * @entityId The entity reference to check
   */
  public suspend fun ApplicationCall.checkFavorite(entityId: String): CheckFavoriteOutput

  public suspend fun ApplicationCall.getFavorites(): GetFavoritesOutput

  /**
   * @entityId The entity reference to remove
   */
  public suspend fun ApplicationCall.removeFavorite(entityId: String): RemoveFavoriteOutput
}

/**
 * Generated server interface for News
 */
public interface NewsServer {
  public suspend fun ApplicationCall.createNewsItem(body: CreateNewsItemPayload): CreateNewsItemOutput

  /**
   * @id The news item ID
   */
  public suspend fun ApplicationCall.deleteNewsItem(id: String): DeleteNewsItemOutput

  /**
   * @id The news item ID
   */
  public suspend fun ApplicationCall.getNewsItem(id: String): GetNewsItemOutput

  /**
   * @limit Maximum number of items to return (default 20)
   * @nextToken Token for pagination
   */
  public suspend fun ApplicationCall.listNewsItems(limit: Int? = null, nextToken: String? = null): ListNewsItemsOutput

  /**
   * @id The news item ID
   */
  public suspend fun ApplicationCall.updateNewsItem(id: String, body: UpdateNewsItemPayload): UpdateNewsItemOutput
}

/**
 * Generated server interface for Operations
 */
public interface OperationsServer {
  public suspend fun ApplicationCall.getMonitors(
    tag: String,
    page: Int? = null,
    pageSize: Int? = null,
  ): GetMonitorsOutput

  public suspend fun ApplicationCall.queryLogs(
    query: String,
    limit: Int? = null,
    startTs: Instant,
    endTs: Instant,
    paginationToken: String? = null,
    flex: Boolean? = null,
    service: String? = null,
    workload: String? = null,
    region: String? = null,
    environment: String? = null,
  ): QueryLogsOutput

  public suspend fun ApplicationCall.queryMetrics(body: MetricsQueryRequest): QueryMetricsOutput
}

/**
 * Generated server interface for CMCService
 */
public interface CMCServiceServer {
  public val chat: ChatServer

  public val chatTranscripts: ChatTranscriptsServer

  public val favorites: FavoritesServer

  public val news: NewsServer

  public val operations: OperationsServer

  /**
   * @serviceId The identifier of the service, or 'all' for all services
   * @region The region to get the data from. Defaults to the main region. If `all`
   * is passed, returns data from all available regions (typically 3)
   */
  public suspend fun ApplicationCall.getServiceEntity(
    serviceId: String,
    region: String? = null,
    stage: String? = null,
    body: GetServiceEntityPayload,
  ): GetServiceEntityOutput

  public suspend fun ApplicationCall.prepareDirectPlayback(body: DirectPlaybackRequest): PrepareDirectPlaybackOutput

  public suspend fun ApplicationCall.preparePlayback(body: PlaybackRequest): PreparePlaybackOutput
}

/**
 * Route handler for getServiceEntity operation.
 *
 * This function can be used to install a single route independently of the full service interface.
 */
public fun Route.getServiceEntityRoute(handler: suspend ApplicationCall.(
  serviceId: String,
  region: String?,
  stage: String?,
  body: GetServiceEntityPayload,
) -> GetServiceEntityOutput) {
  routingPost("/services/{serviceId}/entities") {
    val result = call.handler(
      call.parameters.getOrFail("serviceId"),
      call.parameters["region"],
      call.parameters["stage"],
      call.receive(),
    )
    if (result != null) {
      call.respond(result.result)
    } else {
      call.respond(HttpStatusCode.OK)
    }
  }
}

/**
 * Route handler for prepareDirectPlayback operation.
 *
 * This function can be used to install a single route independently of the full service interface.
 */
public fun Route.prepareDirectPlaybackRoute(handler: suspend ApplicationCall.(body: DirectPlaybackRequest) -> PrepareDirectPlaybackOutput) {
  routingPost("/playback/direct") {
    try {
      val result = call.handler(
        call.receive(),
      )
      if (result != null) {
        call.respond(result.body)
      } else {
        call.respond(HttpStatusCode.OK)
      }
    } catch (e: PlaybackDependencyFailed) {
      call.respond(HttpStatusCode.fromValue(e.httpStatusCode), e.data)
    } catch (e: PlaybackNotConfigured) {
      call.respond(HttpStatusCode.fromValue(e.httpStatusCode), e.data)
    } catch (e: UnsupportedOrigin) {
      call.respond(HttpStatusCode.fromValue(e.httpStatusCode), e.data)
    } catch (e: UnsupportedPlaybackEntity) {
      call.respond(HttpStatusCode.fromValue(e.httpStatusCode), e.data)
    } catch (e: SmithyClientError) {
      val errorData = mapOf("message" to e.message)
      call.respond(HttpStatusCode.fromValue(e.httpStatusCode), errorData)
    } catch (e: SmithyServerError) {
      val errorData = mapOf("message" to e.message)
      call.respond(HttpStatusCode.fromValue(e.httpStatusCode), errorData)
    }
  }
}

/**
 * Route handler for preparePlayback operation.
 *
 * This function can be used to install a single route independently of the full service interface.
 */
public fun Route.preparePlaybackRoute(handler: suspend ApplicationCall.(body: PlaybackRequest) -> PreparePlaybackOutput) {
  routingPost("/playback") {
    try {
      val result = call.handler(
        call.receive(),
      )
      if (result != null) {
        call.respond(result.body)
      } else {
        call.respond(HttpStatusCode.OK)
      }
    } catch (e: AmbiguousPlaybackEntity) {
      call.respond(HttpStatusCode.fromValue(e.httpStatusCode), e.data)
    } catch (e: PlaybackDependencyFailed) {
      call.respond(HttpStatusCode.fromValue(e.httpStatusCode), e.data)
    } catch (e: PlaybackEntityNotFound) {
      call.respond(HttpStatusCode.fromValue(e.httpStatusCode), e.data)
    } catch (e: PlaybackNotConfigured) {
      call.respond(HttpStatusCode.fromValue(e.httpStatusCode), e.data)
    } catch (e: SubscriberConnectionRequired) {
      call.respond(HttpStatusCode.fromValue(e.httpStatusCode), e.data)
    } catch (e: UnsupportedOrigin) {
      call.respond(HttpStatusCode.fromValue(e.httpStatusCode), e.data)
    } catch (e: UnsupportedPlaybackEntity) {
      call.respond(HttpStatusCode.fromValue(e.httpStatusCode), e.data)
    } catch (e: SmithyClientError) {
      val errorData = mapOf("message" to e.message)
      call.respond(HttpStatusCode.fromValue(e.httpStatusCode), errorData)
    } catch (e: SmithyServerError) {
      val errorData = mapOf("message" to e.message)
      call.respond(HttpStatusCode.fromValue(e.httpStatusCode), errorData)
    }
  }
}

/**
 * Route handler for deleteChat operation.
 *
 * This function can be used to install a single route independently of the full service interface.
 */
public fun Route.deleteChatRoute(handler: suspend ApplicationCall.(conversationId: String) -> DeleteChatOutput) {
  routingDelete("/chat/{conversationId}") {
    try {
      val result = call.handler(
        call.parameters.getOrFail("conversationId"),
      )
      if (result != null) {
        call.respond(result)
      } else {
        call.respond(HttpStatusCode.OK)
      }
    } catch (e: ChatNotFound) {
      call.respond(HttpStatusCode.fromValue(e.httpStatusCode), e.data)
    } catch (e: SmithyClientError) {
      val errorData = mapOf("message" to e.message)
      call.respond(HttpStatusCode.fromValue(e.httpStatusCode), errorData)
    } catch (e: SmithyServerError) {
      val errorData = mapOf("message" to e.message)
      call.respond(HttpStatusCode.fromValue(e.httpStatusCode), errorData)
    }
  }
}

/**
 * Route handler for getChat operation.
 *
 * This function can be used to install a single route independently of the full service interface.
 */
public fun Route.getChatRoute(handler: suspend ApplicationCall.(conversationId: String) -> GetChatOutput) {
  routingGet("/chat/{conversationId}") {
    try {
      val result = call.handler(
        call.parameters.getOrFail("conversationId"),
      )
      if (result != null) {
        call.respond(result.body)
      } else {
        call.respond(HttpStatusCode.OK)
      }
    } catch (e: ChatNotFound) {
      call.respond(HttpStatusCode.fromValue(e.httpStatusCode), e.data)
    } catch (e: SmithyClientError) {
      val errorData = mapOf("message" to e.message)
      call.respond(HttpStatusCode.fromValue(e.httpStatusCode), errorData)
    } catch (e: SmithyServerError) {
      val errorData = mapOf("message" to e.message)
      call.respond(HttpStatusCode.fromValue(e.httpStatusCode), errorData)
    }
  }
}

/**
 * Route handler for listChats operation.
 *
 * This function can be used to install a single route independently of the full service interface.
 */
public fun Route.listChatsRoute(handler: suspend ApplicationCall.(limit: Int?) -> ListChatsOutput) {
  routingGet("/chat") {
    val result = call.handler(
      call.parameters["limit"]?.toInt(),
    )
    if (result != null) {
      call.respond(result.body)
    } else {
      call.respond(HttpStatusCode.OK)
    }
  }
}

/**
 * Route handler for startChat operation.
 *
 * This function can be used to install a single route independently of the full service interface.
 */
public fun Route.startChatRoute(handler: suspend ApplicationCall.(body: StartChatPayload) -> StartChatOutput) {
  routingPost("/chat") {
    val result = call.handler(
      call.receive(),
    )
    if (result != null) {
      call.respond(result.result)
    } else {
      call.respond(HttpStatusCode.OK)
    }
  }
}

/**
 * Route handler for getChatTranscript operation.
 *
 * This function can be used to install a single route independently of the full service interface.
 */
public fun Route.getChatTranscriptRoute(handler: suspend ApplicationCall.(conversationId: String) -> GetChatTranscriptOutput) {
  routingGet("/chat-transcripts/{conversationId}") {
    try {
      val result = call.handler(
        call.parameters.getOrFail("conversationId"),
      )
      if (result != null) {
        call.respond(result.body)
      } else {
        call.respond(HttpStatusCode.OK)
      }
    } catch (e: ChatNotFound) {
      call.respond(HttpStatusCode.fromValue(e.httpStatusCode), e.data)
    } catch (e: SmithyClientError) {
      val errorData = mapOf("message" to e.message)
      call.respond(HttpStatusCode.fromValue(e.httpStatusCode), errorData)
    } catch (e: SmithyServerError) {
      val errorData = mapOf("message" to e.message)
      call.respond(HttpStatusCode.fromValue(e.httpStatusCode), errorData)
    }
  }
}

/**
 * Route handler for listChatTranscripts operation.
 *
 * This function can be used to install a single route independently of the full service interface.
 */
public fun Route.listChatTranscriptsRoute(handler: suspend ApplicationCall.(limit: Int?, userId: String?) -> ListChatTranscriptsOutput) {
  routingGet("/chat-transcripts") {
    val result = call.handler(
      call.parameters["limit"]?.toInt(),
      call.parameters["userId"],
    )
    if (result != null) {
      call.respond(result.body)
    } else {
      call.respond(HttpStatusCode.OK)
    }
  }
}

/**
 * Route handler for addFavorite operation.
 *
 * This function can be used to install a single route independently of the full service interface.
 */
public fun Route.addFavoriteRoute(handler: suspend ApplicationCall.(body: AddFavoritePayload) -> AddFavoriteOutput) {
  routingPost("/favorites") {
    val result = call.handler(
      call.receive(),
    )
    if (result != null) {
      call.respond(result)
    } else {
      call.respond(HttpStatusCode.OK)
    }
  }
}

/**
 * Route handler for checkFavorite operation.
 *
 * This function can be used to install a single route independently of the full service interface.
 */
public fun Route.checkFavoriteRoute(handler: suspend ApplicationCall.(entityId: String) -> CheckFavoriteOutput) {
  routingGet("/favorites/{entityId}") {
    val result = call.handler(
      call.parameters.getOrFail("entityId"),
    )
    if (result != null) {
      call.respond(result)
    } else {
      call.respond(HttpStatusCode.OK)
    }
  }
}

/**
 * Route handler for getFavorites operation.
 *
 * This function can be used to install a single route independently of the full service interface.
 */
public fun Route.getFavoritesRoute(handler: suspend ApplicationCall.() -> GetFavoritesOutput) {
  routingGet("/favorites") {
    val result = call.handler(
    )
    if (result != null) {
      call.respond(result.favorites)
    } else {
      call.respond(HttpStatusCode.OK)
    }
  }
}

/**
 * Route handler for removeFavorite operation.
 *
 * This function can be used to install a single route independently of the full service interface.
 */
public fun Route.removeFavoriteRoute(handler: suspend ApplicationCall.(entityId: String) -> RemoveFavoriteOutput) {
  routingDelete("/favorites/{entityId}") {
    val result = call.handler(
      call.parameters.getOrFail("entityId"),
    )
    if (result != null) {
      call.respond(result)
    } else {
      call.respond(HttpStatusCode.OK)
    }
  }
}

/**
 * Route handler for createNewsItem operation.
 *
 * This function can be used to install a single route independently of the full service interface.
 */
public fun Route.createNewsItemRoute(handler: suspend ApplicationCall.(body: CreateNewsItemPayload) -> CreateNewsItemOutput) {
  routingPost("/news") {
    val result = call.handler(
      call.receive(),
    )
    if (result != null) {
      call.respond(result.item)
    } else {
      call.respond(HttpStatusCode.OK)
    }
  }
}

/**
 * Route handler for deleteNewsItem operation.
 *
 * This function can be used to install a single route independently of the full service interface.
 */
public fun Route.deleteNewsItemRoute(handler: suspend ApplicationCall.(id: String) -> DeleteNewsItemOutput) {
  routingDelete("/news/{id}") {
    val result = call.handler(
      call.parameters.getOrFail("id"),
    )
    if (result != null) {
      call.respond(result)
    } else {
      call.respond(HttpStatusCode.OK)
    }
  }
}

/**
 * Route handler for getNewsItem operation.
 *
 * This function can be used to install a single route independently of the full service interface.
 */
public fun Route.getNewsItemRoute(handler: suspend ApplicationCall.(id: String) -> GetNewsItemOutput) {
  routingGet("/news/{id}") {
    val result = call.handler(
      call.parameters.getOrFail("id"),
    )
    if (result != null) {
      call.respond(result.item)
    } else {
      call.respond(HttpStatusCode.OK)
    }
  }
}

/**
 * Route handler for listNewsItems operation.
 *
 * This function can be used to install a single route independently of the full service interface.
 */
public fun Route.listNewsItemsRoute(handler: suspend ApplicationCall.(limit: Int?, nextToken: String?) -> ListNewsItemsOutput) {
  routingGet("/news") {
    val result = call.handler(
      call.parameters["limit"]?.toInt(),
      call.parameters["nextToken"],
    )
    if (result != null) {
      call.respond(result.body)
    } else {
      call.respond(HttpStatusCode.OK)
    }
  }
}

/**
 * Route handler for updateNewsItem operation.
 *
 * This function can be used to install a single route independently of the full service interface.
 */
public fun Route.updateNewsItemRoute(handler: suspend ApplicationCall.(id: String, body: UpdateNewsItemPayload) -> UpdateNewsItemOutput) {
  routingPut("/news/{id}") {
    val result = call.handler(
      call.parameters.getOrFail("id"),
      call.receive(),
    )
    if (result != null) {
      call.respond(result.item)
    } else {
      call.respond(HttpStatusCode.OK)
    }
  }
}

/**
 * Route handler for getMonitors operation.
 *
 * This function can be used to install a single route independently of the full service interface.
 */
public fun Route.getMonitorsRoute(handler: suspend ApplicationCall.(
  tag: String,
  page: Int?,
  pageSize: Int?,
) -> GetMonitorsOutput) {
  routingGet("/monitors") {
    val result = call.handler(
      call.parameters.getOrFail("tag"),
      call.parameters["page"]?.toInt(),
      call.parameters["pageSize"]?.toInt(),
    )
    if (result != null) {
      call.respond(result.body)
    } else {
      call.respond(HttpStatusCode.OK)
    }
  }
}

/**
 * Route handler for queryLogs operation.
 *
 * This function can be used to install a single route independently of the full service interface.
 */
public fun Route.queryLogsRoute(handler: suspend ApplicationCall.(
  query: String,
  limit: Int?,
  startTs: Instant,
  endTs: Instant,
  paginationToken: String?,
  flex: Boolean?,
  service: String?,
  workload: String?,
  region: String?,
  environment: String?,
) -> QueryLogsOutput) {
  routingGet("/logs") {
    val result = call.handler(
      call.parameters.getOrFail("query"),
      call.parameters["limit"]?.toInt(),
      call.parameters.getOrFail("startTs").let { Instant.parse(it) },
      call.parameters.getOrFail("endTs").let { Instant.parse(it) },
      call.parameters["paginationToken"],
      call.parameters["flex"]?.toBoolean(),
      call.parameters["service"],
      call.parameters["workload"],
      call.parameters["region"],
      call.parameters["environment"],
    )
    if (result != null) {
      call.respond(result.body)
    } else {
      call.respond(HttpStatusCode.OK)
    }
  }
}

/**
 * Route handler for queryMetrics operation.
 *
 * This function can be used to install a single route independently of the full service interface.
 */
public fun Route.queryMetricsRoute(handler: suspend ApplicationCall.(body: MetricsQueryRequest) -> QueryMetricsOutput) {
  routingPost("/metrics/query") {
    val result = call.handler(
      call.receive(),
    )
    if (result != null) {
      call.respond(result.body)
    } else {
      call.respond(HttpStatusCode.OK)
    }
  }
}

/**
 * Routing tree for CMCService
 */
public fun Route.CMCServiceRouting(_service: CMCServiceServer) {
  getServiceEntityRoute { serviceId, region, stage, body -> with(_service) { getServiceEntity(serviceId, region, stage, body) } }
  prepareDirectPlaybackRoute { body -> with(_service) { prepareDirectPlayback(body) } }
  preparePlaybackRoute { body -> with(_service) { preparePlayback(body) } }
  deleteChatRoute { conversationId -> with(_service.chat) { deleteChat(conversationId) } }
  getChatRoute { conversationId -> with(_service.chat) { getChat(conversationId) } }
  listChatsRoute { limit -> with(_service.chat) { listChats(limit) } }
  startChatRoute { body -> with(_service.chat) { startChat(body) } }
  getChatTranscriptRoute { conversationId -> with(_service.chatTranscripts) { getChatTranscript(conversationId) } }
  listChatTranscriptsRoute { limit, userId -> with(_service.chatTranscripts) { listChatTranscripts(limit, userId) } }
  addFavoriteRoute { body -> with(_service.favorites) { addFavorite(body) } }
  checkFavoriteRoute { entityId -> with(_service.favorites) { checkFavorite(entityId) } }
  getFavoritesRoute {  -> with(_service.favorites) { getFavorites() } }
  removeFavoriteRoute { entityId -> with(_service.favorites) { removeFavorite(entityId) } }
  createNewsItemRoute { body -> with(_service.news) { createNewsItem(body) } }
  deleteNewsItemRoute { id -> with(_service.news) { deleteNewsItem(id) } }
  getNewsItemRoute { id -> with(_service.news) { getNewsItem(id) } }
  listNewsItemsRoute { limit, nextToken -> with(_service.news) { listNewsItems(limit, nextToken) } }
  updateNewsItemRoute { id, body -> with(_service.news) { updateNewsItem(id, body) } }
  getMonitorsRoute { tag, page, pageSize -> with(_service.operations) { getMonitors(tag, page, pageSize) } }
  queryLogsRoute { query, limit, startTs, endTs, paginationToken, flex, service, workload, region, environment -> with(_service.operations) { queryLogs(query, limit, startTs, endTs, paginationToken, flex, service, workload, region, environment) } }
  queryMetricsRoute { body -> with(_service.operations) { queryMetrics(body) } }
}

/**
 * A chat conversation with its current status and result
 */
@Serializable
public data class ChatConversation(
  /**
   * Unique identifier
   */
  public val conversationId: String,
  /**
   * Current processing status
   */
  public val status: ChatStatus,
  /**
   * Human-readable status message (present while PENDING or PROCESSING)
   */
  public val statusMessage: String? = null,
  /**
   * The messages sent in this conversation
   */
  public val messages: ChatMessageList? = null,
  /**
   * The assistant's markdown response (present when COMPLETED)
   */
  public val response: String? = null,
  /**
   * Error message (present when FAILED)
   */
  public val error: String? = null,
  /**
   * When the conversation was created (ISO8601)
   */
  public val createdAt: String? = null,
  /**
   * When the conversation was last updated (ISO8601)
   */
  public val updatedAt: String? = null,
)

/**
 * Summary of a conversation shown in the history sidebar
 */
@Serializable
public data class ChatListItem(
  public val conversationId: String,
  public val status: ChatStatus,
  public val statusMessage: String? = null,
  /**
   * Preview text from the first user message (up to 100 characters)
   */
  public val preview: String? = null,
  /**
   * When the conversation was created (ISO8601)
   */
  public val createdAt: String? = null,
)

public typealias ChatListItemList = List<ChatListItem>

/**
 * A message in a chat conversation
 */
@Serializable
public data class ChatMessage(
  /**
   * Role: "user" or "assistant"
   */
  public val role: String,
  /**
   * Message content
   */
  public val content: String? = null,
)

public typealias ChatMessageList = List<ChatMessage>

/**
 * Error when a conversation is not found
 */
@Serializable
public data class ChatNotFoundData(
  public val message: String,
  public val conversationId: String? = null,
)

/**
 * Error when a conversation is not found
 */
public class ChatNotFound(
  public val `data`: ChatNotFoundData,
) : SmithyClientError(data.message, httpStatusCode = 404) {
  override val message: String
    get() = data.message

  public val conversationId: String?
    get() = data.conversationId

  public constructor(message: String, conversationId: String? = null) : this(ChatNotFoundData(message, conversationId))
}

/**
 * Processing status for a chat conversation
 */
@Serializable(with = ChatStatusSerializer::class)
public sealed class ChatStatus(
  override val name: String,
  public val key: String,
) : SmithyEnum {
  override fun toString(): String = name

  /**
   * Request received, queued for processing
   */
  public object PENDING : ChatStatus("PENDING", "PENDING")

  /**
   * Worker is actively processing
   */
  public object PROCESSING : ChatStatus("PROCESSING", "PROCESSING")

  /**
   * Successfully completed
   */
  public object COMPLETED : ChatStatus("COMPLETED", "COMPLETED")

  /**
   * Processing failed
   */
  public object FAILED : ChatStatus("FAILED", "FAILED")

  public class _Unknown(
    name: String,
  ) : ChatStatus(name, ""),
      SmithyUnknown

  public companion object : SmithyEnumCompanion<ChatStatus> {
    override val entries: List<ChatStatus> by
        lazy { listOf(PENDING, PROCESSING, COMPLETED, FAILED) }

    override fun fromName(name: String): ChatStatus = when (name) {
      "PENDING" -> PENDING
      "PROCESSING" -> PROCESSING
      "COMPLETED" -> COMPLETED
      "FAILED" -> FAILED
      else -> _Unknown(name)
    }

    override fun valueOf(name: String): ChatStatus = fromName(name)

    public fun fromKey(key: String): ChatStatus = when (key) {
      "PENDING" -> PENDING
      "PROCESSING" -> PROCESSING
      "COMPLETED" -> COMPLETED
      "FAILED" -> FAILED
      else -> throw IllegalArgumentException("""No enum key $key exists in ChatStatus""")
    }
  }
}

public object ChatStatusSerializer : KSerializer<ChatStatus> {
  override val descriptor: SerialDescriptor =
      PrimitiveSerialDescriptor("ChatStatus", PrimitiveKind.STRING)

  override fun serialize(encoder: Encoder, `value`: ChatStatus) {
    encoder.encodeString(value.name)
  }

  override fun deserialize(decoder: Decoder): ChatStatus = ChatStatus.fromName(decoder.decodeString())
}

/**
 * The full captured transcript of a conversation
 */
@Serializable
public data class ChatTranscript(
  public val conversationId: String,
  /**
   * Cognito sub of the user who started the conversation
   */
  public val userId: String? = null,
  /**
   * Current processing status
   */
  public val status: ChatStatus? = null,
  /**
   * The complete ordered message list, including tool calls and tool results
   */
  public val messages: TranscriptMessageList,
  /**
   * When the conversation was created (ISO8601)
   */
  public val createdAt: String? = null,
  /**
   * When the transcript was captured (ISO8601)
   */
  public val capturedAt: String? = null,
)

/**
 * Summary of a conversation shown in the admin transcript list
 */
@Serializable
public data class ChatTranscriptListItem(
  public val conversationId: String,
  /**
   * Cognito sub of the user who started the conversation
   */
  public val userId: String? = null,
  public val status: ChatStatus,
  /**
   * Preview text from the first user message (up to 100 characters)
   */
  public val preview: String? = null,
  /**
   * When the conversation was created (ISO8601)
   */
  public val createdAt: String? = null,
)

public typealias ChatTranscriptListItemList = List<ChatTranscriptListItem>

@Serializable
public data class CheckFavoriteInput(
  /**
   * The entity reference to check
   */
  public val entityId: String,
)

@Serializable
public data class CheckFavoriteOutput(
  /**
   * Whether the entity is a favorite
   */
  public val isFavorite: Boolean? = null,
)

@Serializable
public data class CreateNewsItemInput(
  public val body: CreateNewsItemPayload,
)

@Serializable
public data class CreateNewsItemOutput(
  public val item: NewsItem,
)

@Serializable
public data class CreateNewsItemPayload(
  /**
   * The news content in markdown format
   */
  public val markdown: String,
)

@Serializable
public data class DeleteChatInput(
  public val conversationId: String,
)

@Serializable
public data class DeleteChatOutput(
  public val success: Boolean,
)

@Serializable
public data class DeleteNewsItemInput(
  /**
   * The news item ID
   */
  public val id: String,
)

@Serializable
public data class DeleteNewsItemOutput(
  /**
   * Whether the deletion succeeded
   */
  public val success: Boolean? = null,
)

/**
 * Playback details supplied directly by the caller for content not resolvable through Aggregator.
 */
@Serializable
public data class DirectPlaybackRequest(
  /**
   * The entity type that selects the playback mechanism. Only `episode-audio` (AOD) is supported today.
   */
  public val entityType: String,
  /**
   * The bucket-relative AOD media path, shaped `/{channelId}/1/{episodeId}` — the same value an
   * `episode-audio` entity carries in `media[0].url`. The server derives the signed S3 key from it.
   */
  public val url: String,
  public val title: String? = null,
  public val subtitle: String? = null,
  public val imageUrl: String? = null,
  public val durationMilliseconds: Long? = null,
)

public typealias FavoritesList = List<String>

public typealias GenericEntity = JsonElement

@Serializable
public data class GetChatInput(
  public val conversationId: String,
)

@Serializable
public data class GetChatOutput(
  public val body: ChatConversation,
)

@Serializable
public data class GetChatTranscriptInput(
  public val conversationId: String,
)

@Serializable
public data class GetChatTranscriptOutput(
  public val body: ChatTranscript,
)

@Serializable
public data class GetFavoritesOutput(
  /**
   * List of entity references in format "<type>:<id>"
   */
  public val favorites: FavoritesList,
)

@Serializable
public data class GetMonitorsInput(
  public val tag: String,
  public val page: Int? = null,
  public val pageSize: Int? = null,
)

@Serializable
public data class GetMonitorsOutput(
  public val body: ListMonitorsResponse,
)

@Serializable
public data class GetNewsItemInput(
  /**
   * The news item ID
   */
  public val id: String,
)

@Serializable
public data class GetNewsItemOutput(
  public val item: NewsItem,
)

@Serializable
public data class GetServiceEntityInput(
  /**
   * The identifier of the service, or 'all' for all services
   */
  public val serviceId: String,
  /**
   * The region to get the data from. Defaults to the main region. If `all`
   * is passed, returns data from all available regions (typically 3)
   */
  public val region: String? = null,
  public val stage: String? = null,
  public val body: GetServiceEntityPayload,
)

@Serializable
public data class GetServiceEntityOutput(
  public val result: ServiceEntityList,
)

@Serializable
public data class GetServiceEntityPayload(
  public val entity: JsonElement? = null,
)

@Serializable
public data class ListChatTranscriptsInput(
  /**
   * Maximum number of conversations to return (default 50)
   */
  public val limit: Int? = null,
  /**
   * Optional filter to a single user's conversations (Cognito sub)
   */
  public val userId: String? = null,
)

@Serializable
public data class ListChatTranscriptsOutput(
  public val body: ListChatTranscriptsResponse,
)

/**
 * Response structure for listing transcripts
 */
@Serializable
public data class ListChatTranscriptsResponse(
  public val transcripts: ChatTranscriptListItemList,
)

@Serializable
public data class ListChatsInput(
  /**
   * Maximum number of conversations to return (default 20)
   */
  public val limit: Int? = null,
)

@Serializable
public data class ListChatsOutput(
  public val body: ListChatsResponse,
)

/**
 * Response structure for listing conversations
 */
@Serializable
public data class ListChatsResponse(
  public val conversations: ChatListItemList,
)

@Serializable
public data class ListMonitorsResponse(
  public val monitors: MonitorList? = null,
  /**
   * Total number of monitors matching the query
   */
  public val totalCount: Int? = null,
  /**
   * Total number of pages
   */
  public val pageCount: Int? = null,
)

@Serializable
public data class ListNewsItemsInput(
  /**
   * Maximum number of items to return (default 20)
   */
  public val limit: Int? = null,
  /**
   * Token for pagination
   */
  public val nextToken: String? = null,
)

@Serializable
public data class ListNewsItemsOutput(
  public val body: ListNewsItemsResponse,
)

/**
 * Response structure for listing news items
 */
@Serializable
public data class ListNewsItemsResponse(
  /**
   * List of news items
   */
  public val items: NewsItemList,
  /**
   * Token for next page, if more items exist
   */
  public val nextToken: String? = null,
)

/**
 * Defines an individual log item
 */
@Serializable
public data class Log(
  /**
   * The id of the log. Can be used for pagination
   */
  public val id: String,
  /**
   * A list of tags connected to the log
   */
  public val tags: LogTagsList? = null,
  /**
   * The service associated with this log
   */
  public val service: String? = null,
  public val timestamp: Instant,
  /**
   * A JSON document of attributes
   */
  public val attributes: LogAttributes? = null,
)

public typealias LogAttributes = JsonElement

public typealias LogList = List<Log>

public typealias LogTagsList = List<String>

/**
 * A single timestamped value in a metric timeseries.
 */
@Serializable
public data class MetricPoint(
  /**
   * Unix epoch milliseconds.
   */
  public val timestamp: Long,
  /**
   * Metric value at this timestamp. Null indicates no data (gap in the series).
   */
  public val `value`: Double? = null,
)

public typealias MetricPointList = List<MetricPoint>

/**
 * A single named DataDog metrics query.
 */
@Serializable
public data class MetricQuery(
  /**
   * Reference name used to tag this query's series in the response.
   */
  public val name: String,
  /**
   * DataDog metrics query string.
   * Example: "p99:app_uni_content_catalog.entity.publish.delay{env:prod} by {entity_type}"
   */
  public val query: String,
)

public typealias MetricQueryList = List<MetricQuery>

/**
 * A single timeseries returned from DataDog.
 */
@Serializable
public data class MetricSeries(
  /**
   * The name of the query that produced this series.
   */
  public val queryName: String? = null,
  /**
   * The metric identifier returned by DataDog (e.g., "app_uni_content.publish.delay").
   */
  public val metric: String? = null,
  /**
   * The group scope for "by {}" queries (e.g., "entity_type:show").
   * Empty string if no grouping was applied.
   */
  public val scope: String? = null,
  /**
   * Human-readable display name for the series.
   */
  public val displayName: String? = null,
  /**
   * Ordered list of data points for this series.
   */
  public val points: MetricPointList? = null,
  /**
   * Unit label for the values (e.g., "second", "request").
   */
  public val unit: String? = null,
)

public typealias MetricSeriesList = List<MetricSeries>

/**
 * Request body for a metrics query.
 */
@Serializable
public data class MetricsQueryRequest(
  /**
   * One or more named metric queries. The name is used to identify
   * each query's series in the response so callers can map colors
   * and labels to specific queries.
   */
  public val queries: MetricQueryList,
  /**
   * Start of the time range (required).
   */
  public val from: Instant,
  /**
   * End of the time range (required).
   */
  public val to: Instant,
)

/**
 * Response containing all timeseries returned for the requested queries.
 */
@Serializable
public data class MetricsQueryResponse(
  /**
   * All returned timeseries, one entry per (queryName × scope group).
   * A single query with a "by {tag}" clause may produce multiple series.
   */
  public val series: MetricSeriesList? = null,
  /**
   * DataDog Metrics Explorer URL corresponding to the first query in the request.
   */
  public val url: String? = null,
)

@Serializable
public data class Monitor(
  public val id: String? = null,
  public val name: String? = null,
  public val type: String? = null,
  public val query: String? = null,
  public val status: MonitorState? = null,
  /**
   * Monitor priority (1-5, where 1 is highest/P1)
   */
  public val priority: Int? = null,
)

public typealias MonitorList = List<Monitor>

@Serializable(with = MonitorStateSerializer::class)
public sealed class MonitorState(
  override val name: String,
  public val key: String,
) : SmithyEnum {
  override fun toString(): String = name

  public object OK : MonitorState("OK", "OK")

  public object ALERT : MonitorState("Alert", "ALERT")

  public object WARN : MonitorState("Warn", "WARN")

  public object NO_DATA : MonitorState("No Data", "NO_DATA")

  public object IGNORED : MonitorState("Ignored", "IGNORED")

  public object SKIPPED : MonitorState("Skipped", "SKIPPED")

  public class _Unknown(
    name: String,
  ) : MonitorState(name, ""),
      SmithyUnknown

  public companion object : SmithyEnumCompanion<MonitorState> {
    override val entries: List<MonitorState> by
        lazy { listOf(OK, ALERT, WARN, NO_DATA, IGNORED, SKIPPED) }

    override fun fromName(name: String): MonitorState = when (name) {
      "OK" -> OK
      "Alert" -> ALERT
      "Warn" -> WARN
      "No Data" -> NO_DATA
      "Ignored" -> IGNORED
      "Skipped" -> SKIPPED
      else -> _Unknown(name)
    }

    override fun valueOf(name: String): MonitorState = fromName(name)

    public fun fromKey(key: String): MonitorState = when (key) {
      "OK" -> OK
      "ALERT" -> ALERT
      "WARN" -> WARN
      "NO_DATA" -> NO_DATA
      "IGNORED" -> IGNORED
      "SKIPPED" -> SKIPPED
      else -> throw IllegalArgumentException("""No enum key $key exists in MonitorState""")
    }
  }
}

public object MonitorStateSerializer : KSerializer<MonitorState> {
  override val descriptor: SerialDescriptor =
      PrimitiveSerialDescriptor("MonitorState", PrimitiveKind.STRING)

  override fun serialize(encoder: Encoder, `value`: MonitorState) {
    encoder.encodeString(value.name)
  }

  override fun deserialize(decoder: Decoder): MonitorState = MonitorState.fromName(decoder.decodeString())
}

/**
 * A news item with markdown content and metadata
 */
@Serializable
public data class NewsItem(
  /**
   * Unique identifier for the news item
   */
  public val id: String,
  /**
   * The news content in markdown format
   */
  public val markdown: String,
  /**
   * When the news item was created (ISO8601)
   */
  public val createdAt: String,
  /**
   * When the news item was last updated (ISO8601)
   */
  public val updatedAt: String? = null,
  /**
   * Email of the admin who last updated
   */
  public val updatedBy: String? = null,
)

public typealias NewsItemList = List<NewsItem>

@Serializable(with = PlaybackAccessSerializer::class)
public sealed class PlaybackAccess(
  override val name: String,
  public val key: String,
) : SmithyEnum {
  override fun toString(): String = name

  public object NONE : PlaybackAccess("NONE", "NONE")

  public object RESPONSE_COOKIES : PlaybackAccess("RESPONSE_COOKIES", "RESPONSE_COOKIES")

  public object SIGNED_URL : PlaybackAccess("SIGNED_URL", "SIGNED_URL")

  public class _Unknown(
    name: String,
  ) : PlaybackAccess(name, ""),
      SmithyUnknown

  public companion object : SmithyEnumCompanion<PlaybackAccess> {
    override val entries: List<PlaybackAccess> by
        lazy { listOf(NONE, RESPONSE_COOKIES, SIGNED_URL) }

    override fun fromName(name: String): PlaybackAccess = when (name) {
      "NONE" -> NONE
      "RESPONSE_COOKIES" -> RESPONSE_COOKIES
      "SIGNED_URL" -> SIGNED_URL
      else -> _Unknown(name)
    }

    override fun valueOf(name: String): PlaybackAccess = fromName(name)

    public fun fromKey(key: String): PlaybackAccess = when (key) {
      "NONE" -> NONE
      "RESPONSE_COOKIES" -> RESPONSE_COOKIES
      "SIGNED_URL" -> SIGNED_URL
      else -> throw IllegalArgumentException("""No enum key $key exists in PlaybackAccess""")
    }
  }
}

public object PlaybackAccessSerializer : KSerializer<PlaybackAccess> {
  override val descriptor: SerialDescriptor =
      PrimitiveSerialDescriptor("PlaybackAccess", PrimitiveKind.STRING)

  override fun serialize(encoder: Encoder, `value`: PlaybackAccess) {
    encoder.encodeString(value.name)
  }

  override fun deserialize(decoder: Decoder): PlaybackAccess = PlaybackAccess.fromName(decoder.decodeString())
}

/**
 * Aggregator or an entity-owned playback service could not prepare playback.
 */
@Serializable
public data class PlaybackDependencyFailedData(
  public val message: String,
)

/**
 * Aggregator or an entity-owned playback service could not prepare playback.
 */
public class PlaybackDependencyFailed(
  public val `data`: PlaybackDependencyFailedData,
) : SmithyServerError(data.message, httpStatusCode = 502) {
  override val message: String
    get() = data.message

  public constructor(message: String) : this(PlaybackDependencyFailedData(message))
}

/**
 * A versioned, client-neutral description of prepared media.
 */
@Serializable
public data class PlaybackDescriptor(
  public val schemaVersion: Int,
  public val requestedEntity: PlaybackMetadata,
  public val items: PlaybackItemList,
)

/**
 * Aggregator has no current entity for the requested ID.
 */
@Serializable
public data class PlaybackEntityNotFoundData(
  public val message: String,
)

/**
 * Aggregator has no current entity for the requested ID.
 */
public class PlaybackEntityNotFound(
  public val `data`: PlaybackEntityNotFoundData,
) : SmithyClientError(data.message, httpStatusCode = 404) {
  override val message: String
    get() = data.message

  public constructor(message: String) : this(PlaybackEntityNotFoundData(message))
}

/**
 * One ordered item in a playback description.
 */
@Serializable
public data class PlaybackItem(
  public val metadata: PlaybackMetadata,
  public val streams: PlaybackStreamList,
)

public typealias PlaybackItemList = List<PlaybackItem>

@Serializable(with = PlaybackMediaTypeSerializer::class)
public sealed class PlaybackMediaType(
  override val name: String,
  public val key: String,
) : SmithyEnum {
  override fun toString(): String = name

  public object AUDIO : PlaybackMediaType("AUDIO", "AUDIO")

  public class _Unknown(
    name: String,
  ) : PlaybackMediaType(name, ""),
      SmithyUnknown

  public companion object : SmithyEnumCompanion<PlaybackMediaType> {
    override val entries: List<PlaybackMediaType> by lazy { listOf(AUDIO) }

    override fun fromName(name: String): PlaybackMediaType = when (name) {
      "AUDIO" -> AUDIO
      else -> _Unknown(name)
    }

    override fun valueOf(name: String): PlaybackMediaType = fromName(name)

    public fun fromKey(key: String): PlaybackMediaType = when (key) {
      "AUDIO" -> AUDIO
      else -> throw IllegalArgumentException("""No enum key $key exists in PlaybackMediaType""")
    }
  }
}

public object PlaybackMediaTypeSerializer : KSerializer<PlaybackMediaType> {
  override val descriptor: SerialDescriptor =
      PrimitiveSerialDescriptor("PlaybackMediaType", PrimitiveKind.STRING)

  override fun serialize(encoder: Encoder, `value`: PlaybackMediaType) {
    encoder.encodeString(value.name)
  }

  override fun deserialize(decoder: Decoder): PlaybackMediaType = PlaybackMediaType.fromName(decoder.decodeString())
}

/**
 * Presentation metadata derived from the authoritative Aggregator entity.
 */
@Serializable
public data class PlaybackMetadata(
  public val entityId: String,
  public val entityType: String,
  public val title: String? = null,
  public val subtitle: String? = null,
  public val imageUrl: String? = null,
  public val durationMilliseconds: Long? = null,
  /**
   * Live media suppresses finite-duration and seek controls.
   */
  public val isLive: Boolean? = null,
  /**
   * The source supports the separate, read-only playback-details operation.
   */
  public val detailsAvailable: Boolean? = null,
)

/**
 * The selected playback mechanism is not configured on this server.
 */
@Serializable
public data class PlaybackNotConfiguredData(
  public val message: String,
)

/**
 * The selected playback mechanism is not configured on this server.
 */
public class PlaybackNotConfigured(
  public val `data`: PlaybackNotConfiguredData,
) : SmithyServerError(data.message, httpStatusCode = 500) {
  override val message: String
    get() = data.message

  public constructor(message: String) : this(PlaybackNotConfiguredData(message))
}

@Serializable(with = PlaybackProtocolSerializer::class)
public sealed class PlaybackProtocol(
  override val name: String,
  public val key: String,
) : SmithyEnum {
  override fun toString(): String = name

  public object DIRECT_AUDIO : PlaybackProtocol("DIRECT_AUDIO", "DIRECT_AUDIO")

  public object HLS : PlaybackProtocol("HLS", "HLS")

  public class _Unknown(
    name: String,
  ) : PlaybackProtocol(name, ""),
      SmithyUnknown

  public companion object : SmithyEnumCompanion<PlaybackProtocol> {
    override val entries: List<PlaybackProtocol> by lazy { listOf(DIRECT_AUDIO, HLS) }

    override fun fromName(name: String): PlaybackProtocol = when (name) {
      "DIRECT_AUDIO" -> DIRECT_AUDIO
      "HLS" -> HLS
      else -> _Unknown(name)
    }

    override fun valueOf(name: String): PlaybackProtocol = fromName(name)

    public fun fromKey(key: String): PlaybackProtocol = when (key) {
      "DIRECT_AUDIO" -> DIRECT_AUDIO
      "HLS" -> HLS
      else -> throw IllegalArgumentException("""No enum key $key exists in PlaybackProtocol""")
    }
  }
}

public object PlaybackProtocolSerializer : KSerializer<PlaybackProtocol> {
  override val descriptor: SerialDescriptor =
      PrimitiveSerialDescriptor("PlaybackProtocol", PrimitiveKind.STRING)

  override fun serialize(encoder: Encoder, `value`: PlaybackProtocol) {
    encoder.encodeString(value.name)
  }

  override fun deserialize(decoder: Decoder): PlaybackProtocol = PlaybackProtocol.fromName(decoder.decodeString())
}

/**
 * The only information a client supplies when asking CMC to prepare entity playback.
 */
@Serializable
public data class PlaybackRequest(
  public val entityId: String,
)

/**
 * One playable alternative for an item. URLs may already contain short-lived signatures; when
 * `access` is RESPONSE_COOKIES the PreparePlayback HTTP response also sets the required cookies.
 */
@Serializable
public data class PlaybackStream(
  public val mediaType: PlaybackMediaType,
  public val protocol: PlaybackProtocol,
  public val url: String,
  public val mimeType: String? = null,
  public val access: PlaybackAccess,
  public val expiresAt: Instant? = null,
)

public typealias PlaybackStreamList = List<PlaybackStream>

@Serializable
public data class PrepareDirectPlaybackInput(
  public val body: DirectPlaybackRequest,
)

@Serializable
public data class PrepareDirectPlaybackOutput(
  public val body: PlaybackDescriptor,
)

@Serializable
public data class PreparePlaybackInput(
  public val body: PlaybackRequest,
)

@Serializable
public data class PreparePlaybackOutput(
  public val body: PlaybackDescriptor,
)

@Serializable
public data class QueryLogsInput(
  public val query: String,
  public val limit: Int? = null,
  public val startTs: Instant,
  public val endTs: Instant,
  public val paginationToken: String? = null,
  public val flex: Boolean? = null,
  public val service: String? = null,
  public val workload: String? = null,
  public val region: String? = null,
  public val environment: String? = null,
)

@Serializable
public data class QueryLogsOutput(
  public val body: QueryResponse,
)

@Serializable
public data class QueryMetricsInput(
  public val body: MetricsQueryRequest,
)

@Serializable
public data class QueryMetricsOutput(
  public val body: MetricsQueryResponse,
)

@Serializable
public data class QueryResponse(
  public val logs: LogList? = null,
  public val nextToken: String? = null,
  public val url: String? = null,
)

@Serializable
public data class RemoveFavoriteInput(
  /**
   * The entity reference to remove
   */
  public val entityId: String,
)

@Serializable
public data class RemoveFavoriteOutput(
  /**
   * Whether the operation succeeded
   */
  public val success: Boolean? = null,
)

@Serializable
public data class ServiceEntity(
  /**
   * The id of the service returning the data
   */
  public val serviceId: String,
  /**
   * The region that the data is coming from
   */
  public val region: String,
  /**
   * The stage (dev, test, prod)
   */
  public val stage: String,
  /**
   * The response code provided by the service if the entity is unavailable
   */
  public val errorCode: Int? = null,
  /**
   * The reason returned by the server.
   */
  public val errorReasons: String? = null,
  /**
   * The entity. If not included, there was likely an error getting the entity
   */
  public val entity: GenericEntity? = null,
)

public typealias ServiceEntityList = List<ServiceEntity>

@Serializable
public data class StartChatInput(
  public val body: StartChatPayload,
)

@Serializable
public data class StartChatOutput(
  public val result: StartChatResponse,
)

@Serializable
public data class StartChatPayload(
  /**
   * Conversation history including the new user message
   */
  public val messages: ChatMessageList,
  /**
   * Optional custom system prompt (overrides default)
   */
  public val systemPrompt: String? = null,
  /**
   * When set, append this turn to the existing conversation instead of creating a new record.
   * Must be a conversation owned by the calling user; otherwise the request is rejected. Omit to
   * start a new conversation.
   */
  public val conversationId: String? = null,
)

@Serializable
public data class StartChatResponse(
  /**
   * The ID of the newly created conversation
   */
  public val conversationId: String,
)

/**
 * The client must connect/reconnect a subscriber account through the isolated account service.
 */
@Serializable
public data class SubscriberConnectionRequiredData(
  public val message: String,
)

/**
 * The client must connect/reconnect a subscriber account through the isolated account service.
 */
public class SubscriberConnectionRequired(
  public val `data`: SubscriberConnectionRequiredData,
) : SmithyClientError(data.message, httpStatusCode = 428) {
  override val message: String
    get() = data.message

  public constructor(message: String) : this(SubscriberConnectionRequiredData(message))
}

/**
 * A single message in a full transcript. Beyond role+content it carries the tool-call detail the
 * user-facing ChatMessage omits.
 */
@Serializable
public data class TranscriptMessage(
  /**
   * Role: "system", "user", "assistant", or "tool"
   */
  public val role: String,
  /**
   * Message content (assistant prose, user text, or a tool result payload)
   */
  public val content: String? = null,
  /**
   * Tool calls the assistant requested on this turn (present on assistant tool-use turns)
   */
  public val toolCalls: TranscriptToolCallList? = null,
  /**
   * For a tool-result turn, the id of the tool call it answers
   */
  public val toolCallId: String? = null,
  /**
   * For a tool-result turn, the name of the tool that was called
   */
  public val toolName: String? = null,
)

public typealias TranscriptMessageList = List<TranscriptMessage>

/**
 * A tool invocation requested by the assistant
 */
@Serializable
public data class TranscriptToolCall(
  public val id: String,
  public val name: String,
  /**
   * The tool arguments, serialized as a JSON string
   */
  public val arguments: String? = null,
)

public typealias TranscriptToolCallList = List<TranscriptToolCall>

/**
 * The entity references a playback origin that CMC is not configured to serve or sign.
 */
@Serializable
public data class UnsupportedOriginData(
  public val message: String,
)

/**
 * The entity references a playback origin that CMC is not configured to serve or sign.
 */
public class UnsupportedOrigin(
  public val `data`: UnsupportedOriginData,
) : SmithyClientError(data.message, httpStatusCode = 400) {
  override val message: String
    get() = data.message

  public constructor(message: String) : this(UnsupportedOriginData(message))
}

/**
 * CMC has no playback adapter for the resolved entity type.
 */
@Serializable
public data class UnsupportedPlaybackEntityData(
  public val message: String,
)

/**
 * CMC has no playback adapter for the resolved entity type.
 */
public class UnsupportedPlaybackEntity(
  public val `data`: UnsupportedPlaybackEntityData,
) : SmithyClientError(data.message, httpStatusCode = 400) {
  override val message: String
    get() = data.message

  public constructor(message: String) : this(UnsupportedPlaybackEntityData(message))
}

@Serializable
public data class UpdateNewsItemInput(
  /**
   * The news item ID
   */
  public val id: String,
  public val body: UpdateNewsItemPayload,
)

@Serializable
public data class UpdateNewsItemOutput(
  public val item: NewsItem,
)

@Serializable
public data class UpdateNewsItemPayload(
  /**
   * Updated markdown content (optional)
   */
  public val markdown: String? = null,
)
